import moment from "moment";
import * as tConst from "../../Common/Constants";
import { pickColorAdo } from "../../utils/pickColor";
import { formatDate } from "../../utils/formatDate";
import { Ticket } from "../../services/Ticket";
import { getTimelineQuarter, findNextQuarterStart } from "../../utils/DateUtils";
import { PRIORITY_DRIVERS_ALL, PRIORITY_DRIVERS_NONE, PRIORITY_DRIVERS_PRODUCT_ROADMAP, PRIORITY_DRIVERS_TECHNICAL_ROADMAP, RND_PLANNING_PROJECT, TEAMS_LIST, VIEW_ACTIVE_LAST_AND_NEWER } from "../../Common/Constants";

const emptyString = "";


export class TimelineDataResult {
    dataset: any[];
    minDate: Date | null;
    maxDate: Date | null;
    filteredTickets: Ticket[];

    constructor(dataset: any[] = [], minDate: Date | null = null, maxDate: Date | null = null, filteredTickets: Ticket[] = []) {
        this.dataset = dataset;
        this.minDate = minDate;
        this.maxDate = maxDate;
        this.filteredTickets = filteredTickets;
    }
}

export function PrepareTimelineDataAdoSimplified(tickets: Ticket[], teamId : string, selectedPriorityDriver : string, assigneeId: string = "", viewId: string = tConst.VIEW_ALL_PLANNED, quarterFilter : any = null, selectedTags: string[] = [], selectedAreaPath: string = "" ) : TimelineDataResult{
    let dataset :any = [];
    let _minDate : Date = new Date(2999, 11, 31);
    let _maxDate : Date = new Date(1970, 0, 1);

    // Defensive: ensure tickets is always an array
    if (!Array.isArray(tickets) || tickets.length === 0){
        return new TimelineDataResult(dataset, new Date(0), new Date(0));
    }


    //#region View filtering
    let _filteredTickets: Ticket[] = tickets.filter((ticket: Ticket) => {

        // Filter out tickets that are not planned or have no start or end iteration
        // TODO: Look at this and check if the test is good enough - we should check for valid dates too
        if (!ticket.startIteration || !ticket.endIteration) {
            return false; // Skip tickets without start or end iteration
        } 

        if (ticket.startIteration > ticket.endIteration) {
            console.warn(`Ticket ${ticket.id} has startIteration after endIteration. Skipping.`);   
            return false; // Skip tickets with invalid date ranges
        }

        //#region filter on viewId
        const _filterTicketsByViewId  = filterTicketsByViewId(ticket, viewId)
        const _filterTicketsByPriorityDriver = filterTicketsByPriorityDriver(ticket, selectedPriorityDriver, teamId);
        const _filterTicketsByTags = filterTicketsByTags(ticket, selectedTags);
        const _filterTicketsByAreaPath = filterTicketsByAreaPath(ticket, selectedAreaPath, tickets);

        const _filterTicketsByProject = filterTicketsByProject(ticket, (selectedPriorityDriver !== PRIORITY_DRIVERS_ALL ? "" : RND_PLANNING_PROJECT));
        const _result =  _filterTicketsByViewId && _filterTicketsByPriorityDriver && _filterTicketsByProject && _filterTicketsByTags && _filterTicketsByAreaPath;
        return _result;
        //#endregion filter on viewId
    })
    //#endregion View filtering


    //#region Time period quarter

    /**
     * The calculation of the quarter is based on the startIteration and endIteration dates of the tickets.
     * For the time being we are going to use calender dates for the quarters, but in the future we should align to Iteration dates.
     * The calulation is as follows (QS - Quarter start, QE - Quarter end, S - Start date, E - End date):
     *  (QS < S &&  S < QE  || QS < E && E < QE || S < QS && QE < E)
     */

    let qs : Date;
    let qe : Date;


    _filteredTickets = _filteredTickets.filter((ticket: Ticket) => {
        if (!quarterFilter) {
            return true;
        }

        // Use custom quarter logic with true quarter start
        const calendarQuarterStart = moment(quarterFilter.toDate()).startOf('quarter').toDate();
        const quarterStart = getTimelineQuarter(calendarQuarterStart);
        const nextQuarterStart = findNextQuarterStart(quarterFilter.toDate());
        const quarterEnd = new Date(nextQuarterStart.getTime() - 1 * 24 * 60 * 60 * 1000); // End is day before next quarter

        console.log(`Filtering ticket ${ticket.id} [calendarQuarterStart: ${calendarQuarterStart}] by quarter: ${quarterStart.toISOString().slice(0,10)} to ${quarterEnd.toISOString().slice(0,10)}`);

        qs = quarterStart;
        qe = quarterEnd;

        // Quarter: qs to qe, Ticket: startIteration to endIteration
        return ticket.startIteration && ticket.endIteration && 
               ticket.startIteration <= qe && qs <= ticket.endIteration;
    })
    //#endregion Time period quarter
    

    let _dataset: any[] = _filteredTickets.map((ticket:Ticket) => {

        
        _minDate = (ticket.startIteration  &&  ticket.startIteration < _minDate) ?  ticket.startIteration : _minDate;
        _maxDate = (ticket.endIteration && ticket.endIteration > _maxDate) ? ticket.endIteration : _maxDate;
        

        const _dimColor: boolean = shouldDimColor(ticket, teamId || "", assigneeId);

        const prefix = ticket.type === "Epic" ? "E " : ticket.type === "Initiative" ? "I " : ticket.type === "Feature" ? "F " : ticket.type === "Product Backlog Item" ? "P " : "";


        return [
            ticket.groupBy ? ticket.groupBy: ticket.initiative,
            prefix + "[" + ticket.id + "] " + ticket.title,
            createToolTip(ticket),
            pickColorAdo(ticket, _dimColor),
            ticket.startIteration,
            ticket.endIteration
        ];
    });
    return new TimelineDataResult(_dataset, _minDate, _maxDate, _filteredTickets);
}



function shouldDimColor(ticket: Ticket, teamId : string, assigneeId: string = ""): boolean {
    const _projectName = TEAMS_LIST.find((item) => item.team.toLowerCase() === teamId.toLowerCase())?.project || "";

    if (assigneeId !== "" && assigneeId !== tConst.ADO_ASSIGNED_ALL) {
        if (assigneeId === tConst.ADO_UNASSIGNED) {
            if (ticket.assignee !== tConst.ADO_UNASSIGNED) {
                return true; // Dim if ticket is assigned but filter is for unassigned
            }
        } else {
            if (ticket.assignee !== assigneeId) {
                return true; // Dim if ticket assignee doesn't match selected assignee
            }
        }
    }
    
    if (teamId !== ""  ) {
        // There is a team filter
        if (teamId === tConst.VIEW_NO_TEAMS) {
            // vis tickets som ikke er i et teams prosjekt og 
            // tickets som er i RnD Planning prosjekt og ikke har et team
            if (ticket.project?.toLowerCase() === RND_PLANNING_PROJECT.toLowerCase()) {
                return ticket.teamList?.length > 0; // Dim the color if the ticket is not in a team project
            }
            else {
                const a = TEAMS_LIST.some((item) => item.project.toLowerCase() === ticket.project?.toLowerCase() );
                return !a; // Dim the color if the ticket's project does not match any team project
            }


        }
        else if (!(ticket.project?.toLowerCase() === _projectName.toLowerCase() || ticket.teamList?.some((team : any) => team.toLowerCase() === teamId.toLowerCase()))) {
            return true; // Dim the color if the ticket's project does not match the team project
        }
    }
    

    
    return false;
}


function createToolTip(item : Ticket): String{
    return "<div style='white-space: nowrap; margin: 1em;'>"
        + "[" + item.id + "] " + item.title
        + "<br/>Assignee: " + item.assignee
        + "<br/>Status: " + item.state + (item.planningStatus ? "&nbsp / &nbsp" + item.planningStatus : "")
        + "<br/>Priority: " + (item.planningPri ? item.planningPri : "<Unprioritized>") // + " / " + ticket.guestimate + " guestimate"
        + "<br/>Period: " + formatDate(item.startIteration??new Date(0)) + " - " + formatDate(item.endIteration??new Date(0))
        + "<br/>Epic/ Initiativ: " + emptyString + "<epic>" + emptyString + " / " + item.initiative
        + "<br/>Type: " + item.type
        + (item.project && item.project.toLowerCase() === RND_PLANNING_PROJECT.toLowerCase() ? "<br/>Teams: " + item.teamNames : "<br/>Project: " + item.project)
        + (item.tags ? "<br/>Tags: " + item.tags : "")
        +"</div>";
}

function filterTicketsByViewId(ticket: Ticket, viewId: string): boolean {
  if (viewId === tConst.VIEW_ALL_PLANNED) {
    return true;
  } else if (viewId === tConst.VIEW_ACTIVE_AND_CURRENT) {
    return (
      (ticket.endIteration && ticket.endIteration >= new Date()) ||
      (!tConst.ADO_INACTIVE_STATUSES.includes(ticket.state))
    );
  } else if (viewId === VIEW_ACTIVE_LAST_AND_NEWER) {
    const prevSprintDate = new Date();
    prevSprintDate.setDate(prevSprintDate.getDate() - 14);
    return (
      (ticket.endIteration && ticket.endIteration >= prevSprintDate) ||
      (!tConst.ADO_INACTIVE_STATUSES.includes(ticket.state))
    );
  } else if (viewId === tConst.VIEW_ACTIVE) {
    return (
      ticket.state !== undefined && !tConst.ADO_INACTIVE_STATUSES.includes(ticket.state)
    );
  } else if (viewId === tConst.VIEW_DONE) {
    return (
      ticket.state !== undefined && tConst.ADO_INACTIVE_STATUSES.includes(ticket.state)
    );
  } else if (viewId === tConst.VIEW_CURRENT_ITERATION) {
    return (
      ticket.startIteration !== undefined && ticket.startIteration <= new Date() &&
      ticket.endIteration !== undefined && ticket.endIteration >= new Date()
    );
  } else if (viewId === tConst.VIEW_LATE) {
    return (
      ticket.endIteration !== undefined && ticket.endIteration < new Date() &&
      ticket.state !== undefined && !tConst.ADO_INACTIVE_STATUSES.includes(ticket.state)
    );
  } else if (viewId === tConst.VIEW_FUTURE) {
    return (
      ticket.endIteration !== undefined && ticket.endIteration > new Date()
    );
  } else if (viewId === tConst.VIEW_DEMO_CANDIDATE) {
    return ticket.demo === 'Candidate';
  } else if (viewId === tConst.VIEW_DEMO_PLANNED) {
    return ticket.demo === 'Planned';
  } else if (viewId === tConst.VIEW_DEMO_NOT_SET) {
    return !ticket.demo || ticket.demo === '';
  } else {
    console.warn("Unknown viewId:", viewId);
    return true; // Default case, can be extended for other views
  }
}

function filterTicketsByPriorityDriver(ticket: Ticket, selectedPriorityDriver: string, teamId: string): boolean {   
    
    if (selectedPriorityDriver !== "") {
        // FixME: Here is something odd - this must be matched to the priority driver on the ticket... 
        if (ticket.project.toLowerCase() !== RND_PLANNING_PROJECT.toLowerCase() && (
            selectedPriorityDriver.toLowerCase() === PRIORITY_DRIVERS_NONE.toLowerCase() 
            || selectedPriorityDriver.toLowerCase() === PRIORITY_DRIVERS_PRODUCT_ROADMAP.toLowerCase()
            || selectedPriorityDriver.toLowerCase() === PRIORITY_DRIVERS_TECHNICAL_ROADMAP.toLowerCase())            
        ) {
            return false; // Hide tickets that are not in RnD Planning project when selectedPriorityDriver is one of the special values
        }

        
        if (ticket.priorityDriver && ticket.priorityDriver.toLowerCase() !== selectedPriorityDriver.toLowerCase()) {
            return false
        }
    }
    return true;
}

function filterTicketsByProject(ticket: Ticket, selectedProject: string): boolean {   
    
    if (selectedProject !== "") {
        if (ticket.project && ticket.project.toLowerCase() !== selectedProject.toLowerCase()) {
            return false
        }
    }
    return true;
}

function filterTicketsByTags(ticket: Ticket, selectedTags: string[]): boolean {
    if (!selectedTags || selectedTags.length === 0) {
        return true;
    }
    
    return ticket.tagList?.some(tag => selectedTags.includes(tag)) || false;
}

function filterTicketsByAreaPath(ticket: Ticket, selectedAreaPath: string, allTickets: Ticket[]): boolean {
    if (!selectedAreaPath) {
        return true;
    }
    
    // Extract parts from area path (e.g., "RnD Planning\\TeamA" -> ["RnD Planning", "TeamA"])
    const parts = ticket.area?.split('\\') || [];
    const rootArea = parts[0]; // e.g., "RnD Planning"
    const firstLevelAreaPath = parts.length > 1 ? parts[1] : undefined;
    
    // Special case: "__ROOT__" means only items at root level (e.g., "RnD Planning" with no sub-area)
    if (selectedAreaPath === "__ROOT__") {
        // Only include tickets at root level (i.e., area path has no sub-level after the root, e.g., "RnD Planning" but not "RnD Planning\\TeamA")
        if (rootArea && !firstLevelAreaPath) {
            return true;
        }
        // Also include children whose parent is at root
        if (ticket.parent) {
            const parentTicket = allTickets.find(t => t.id === ticket.parent);
            if (parentTicket) {
                const parentParts = parentTicket.area?.split('\\') || [];
                const parentRoot = parentParts[0];
                const parentFirstLevel = parentParts.length > 1 ? parentParts[1] : undefined;
                if (parentRoot && !parentFirstLevel) {
                    return true;
                }
            }
        }
        return false;
    }
    
    // If ticket matches the area path, include it
    if (firstLevelAreaPath === selectedAreaPath) {
        return true;
    }
    
    // If ticket has a parent, check if parent matches the area path
    // This ensures children are included if their parent matches
    if (ticket.parent) {
        const parentTicket = allTickets.find(t => t.id === ticket.parent);
        if (parentTicket) {
            const parentParts = parentTicket.area?.split('\\') || [];
            const parentFirstLevelAreaPath = parentParts.length > 1 ? parentParts[1] : undefined;
            if (parentFirstLevelAreaPath === selectedAreaPath) {
                return true;
            }
        }
    }
    
    return false;
}
